import ImageUpload from "./ImageUpload";
import React, { useState, useRef, useContext, useEffect } from "react";

import { UserCog } from 'lucide-react';

const FormModeCtx = React.createContext("add");

function EditableInput({ name, value, onChange, onBlur, placeholder, disabled }) {
  const mode = useContext(FormModeCtx);
  const [editing, setEditing] = useState(false);
  const locked = mode === "edit" && !editing && !disabled;
  return locked ? (
    <div
      className="flex items-center gap-1 input bg-gray-50 cursor-default overflow-hidden"
      style={{minHeight: "36px"}}
      title={value ? `${value}\n\nClick the pencil icon to edit` : "Click the pencil icon to edit"}
    >
      <span className="flex-1 text-sm text-gray-800 truncate">{value || <span className="text-gray-400 italic">—</span>}</span>
      <button type="button" onClick={() => setEditing(true)}
        className="text-gray-400 shrink-0 transition-colors" style={{ color: 'var(--theme-muted)' }} onMouseEnter={e => e.currentTarget.style.color = 'var(--theme-accent)'} onMouseLeave={e => e.currentTarget.style.color = 'var(--theme-muted)'}>
        <EditIcon size={ICON_SIZE} />
      </button>
    </div>
  ) : (
    <input
      autoFocus={mode === "edit" && editing}
      className="input"
      name={name}
      value={value}
      onChange={onChange}
      onBlur={(e) => { setEditing(false); onBlur(e); }}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
}
import { GlobalContext } from "./GlobalContext";
import { apiFetch } from "./api";
import { fetchPermissionCatalog, computeRoleBase, effectiveWithOverrides } from "./permissions";
import PermissionGrid from "./PermissionGrid";
import ModalTitleBar from "./ModalTitleBar";
import utilities, { formatPhoneForDisplay, formatPhoneForStorage } from "./UtilityFunctions";
import InviteResultDialog from "./InviteResultDialog";
import fields from "./fields";
import { Theme, Button, Flex, Spinner, Text } from "@radix-ui/themes";
import { useToast } from "./ToastContext";
import ReactDOM from "react-dom";
import useDiscardGuard from "./useDiscardGuard";
import { CancelIcon, EditIcon, ICON_SIZE } from "./actionIcons";
const defaultProfilePic = "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png";

const UserAccountModal = ({ user, isOpen, onClose, mode }) => {
  if ( mode==="edit" && (!isOpen || !user?.UserID)){
    //After deleting the user, the user object is null
    console.log("CLosing");
    onClose();
  }
  //Mode is either "add" or "edit"
  const parentSheetName = "UserAccounts";
  //const ownSheetName = "Users";
  const table_name = "Users";
  const primary_key = "UserID";
  const { sendOrFail } = utilities;
  const globals = useContext(GlobalContext);
  const { UserID, socket, toggleMessageBox, getMsgBoxResult, logOut } = globals;
  const { showToast } = useToast();

  // Add loading state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  //Set once the account is created; holds this modal open behind the invite
  //result until the admin has seen (and possibly copied) the link.
  const [inviteResult, setInviteResult] = useState(null);

  const [backupUserData, setBackupUserData] = useState(()=>{
    if(mode==="add"){
      return {
        EmailAddress: "",
        FirstName: "",
        LastName: "",
        Phone1: "",
        Phone2: "",
      }
    } else {
      const returnObj = {};
      Object.getOwnPropertyNames(user).forEach((prop)=>{
        if(prop==="Permissions") return;

        if(user[prop]!==null){
          // Format phone numbers for display
          if(prop === "Phone1" || prop === "Phone2") {
            returnObj[prop] = formatPhoneForDisplay(user[prop]);
          } else {
            returnObj[prop] = user[prop];
          }
        } else {
          returnObj[prop] = "";
        }
      });
      return returnObj;
    }
  });

  const [ isUnique, setIsUnique ] = useState(()=>{
    return {EmailAddress: ''}
  });
  const [ userData, setUserData ] = useState(backupUserData);

  //Roles are dealership-defined (migration 021), so the checkboxes come from
  ///getroles rather than a hardcoded four. `roles` is keyed by RoleName ->
  //{RoleID, Granted}; backupRoles mirrors the last-saved state for rollback.
  const [backupRoles, setBackupRoles] = useState({});
  const [ roles, setRoles ] = useState({});

  //Per-user permission fine-tuning (overrides on top of the role defaults).
  //`rawRoles` keeps /getroles' GrantsAll+Permissions so the role base can be
  //recomputed live as role checkboxes toggle; `catalog` is the server's
  //permission list; `overrides` is { permName: true(grant)|false(deny) } —
  //only the deltas from the role base. `showPerms` reveals the grid ("next" /
  //scroll-down fine-tuning from the UX spec).
  const [rawRoles, setRawRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [overrides, setOverrides] = useState({});
  const [showPerms, setShowPerms] = useState(false);
  const [isSavingPerms, setIsSavingPerms] = useState(false);

  //Discard-guard: add mode accumulates a whole form for one submit; edit mode
  //saves fields and role toggles instantly, so the only edit-mode state that
  //can be lost is permission fine-tuning pending its "Save permissions".
  const confirmLeave = useDiscardGuard();
  const [formTouched, setFormTouched] = useState(false);
  const savedOverridesRef = useRef({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [rolesRes, cat] = await Promise.all([
          apiFetch('/getroles', { body: { DealershipID: globals.DealershipID } }),
          fetchPermissionCatalog(),
        ]);
        const json = await rolesRes.json();
        if (cancelled) return;
        if (cat?.permissions) setCatalog(cat.permissions);
        if (!json.ok) return;
        setRawRoles(json.data);
        const map = {};
        for (const r of json.data) {
          const membership = mode === "edit" ? user?.Permissions?.[r.RoleName] : null;
          const isMember = !!(membership && typeof membership === 'object' && membership.Granted);
          map[r.RoleName] = { RoleID: r.RoleID, Granted: isMember };
        }
        setRoles(map);
        setBackupRoles(map);
      } catch (err) {
        console.error('Failed to load roles/catalog:', err);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //In edit mode, seed the stored per-user overrides so the grid opens showing
  //this user's actual effective permissions (role base +/- overrides).
  useEffect(() => {
    if (mode !== "edit" || !user?.UserID) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch('/getuserpermissions', { body: { UserID: user.UserID } });
        const json = await res.json();
        if (cancelled || !json.ok) return;
        const map = {};
        for (const o of (json.data.overrides || [])) map[o.Permission] = !!o.Effect;
        setOverrides(map);
        savedOverridesRef.current = map;
      } catch (err) {
        console.error('Failed to load user permissions:', err);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.UserID]);
  const [profilePic, setProfilePic] = useState(user?.profilePic || defaultProfilePic);
  
  const [errors, setErrors] = useState({
    EmailAddress: "",
    FirstName: "",
    LastName: "",
    Phone1: "",
    Phone2: "",
  });

  const handleProfileImageChange = (imageUrl) => {
    setProfilePic(imageUrl);
    
    // If in edit mode, update the profile picture in the database
    if (mode === "edit") {
      sendOrFail({
        DealershipID: globals.DealershipID,
        UserID: globals.userData.UserID,
        ownSheetName: parentSheetName,
        table: table_name,
        fieldName: "profilePic",
        fieldValue: imageUrl,
        primaryKeyName: primary_key,
        primaryKeyValue: userData.UserID
      }, socket, userData, backupUserData, setUserData);
    } else {
      // In add mode, just update the userData state
      setFormTouched(true);
      setUserData({ ...userData, profilePic: imageUrl });
    }
  };

  const handleChange = ({ target }) => { //Set the state as user types
    //In edit mode, prevent changes to fields in userData if UserID !== UserData.UserID
    //If target.name is a property in userData, return
    if(mode==="edit" && UserID !==userData.UserID && target.name in userData){
      return;   
    }
    
    const { name, value } = target;
    setFormTouched(true);
    setUserData({ ...userData, [name]: value });
  };

  const handleRoleChange = (role) => {
    //For roles, this may also have to handle submitting
    //the data to the db
    setFormTouched(true);
    setRoles((prev) => {
      const newRole = {...prev[role]};
      newRole.Granted = !newRole.Granted;
      console.log("New role: ", newRole.Granted);
      return {...prev, [role]: newRole};
    });
  };

  //Derived permission state: the role base (recomputed from the selected role
  //checkboxes) and the effective set (base with per-user overrides applied).
  const allPermNames = catalog.map((p) => p.name);
  const selectedRoleNames = new Set(
    Object.entries(roles).filter(([, r]) => r.Granted).map(([name]) => name)
  );
  const roleBase = computeRoleBase(rawRoles, selectedRoleNames, allPermNames);
  const effectivePerms = effectiveWithOverrides(roleBase, new Map(Object.entries(overrides)));
  const canEditPerms = mode === "add" || (globals.can('users.manage') && globals.userData.UserID !== user?.UserID);

  //Toggle one permission: when the new state matches the role default, the
  //override is dropped (follows the role again); otherwise it's stored as a
  //grant/deny delta. Keeping only deltas means later role edits still apply.
  const handlePermToggle = (name) => {
    if (!canEditPerms) return;
    const desired = !effectivePerms.has(name);
    setOverrides((prev) => {
      const next = { ...prev };
      if (desired === roleBase.has(name)) delete next[name];
      else next[name] = desired;
      return next;
    });
  };

  //Unsaved state: in add mode anything typed/toggled since open; in edit mode
  //only permission overrides not yet saved (fields and roles save instantly).
  const overridesEqual = (a, b) => {
    const keys = Object.keys(a);
    if (keys.length !== Object.keys(b).length) return false;
    return keys.every((k) => b[k] === a[k]);
  };
  const hasUnsaved = (mode === "add" && formTouched) || !overridesEqual(overrides, savedOverridesRef.current);
  const requestClose = async () => { if (await confirmLeave(hasUnsaved)) onClose(); };

  //Edit mode: role checkboxes auto-save, but permission fine-tuning is an
  //explicit save (sends the full desired set; the server re-derives the deltas
  //from the user's current role base).
  const handleSavePermissions = async () => {
    if (isSavingPerms) return;
    setIsSavingPerms(true);
    try {
      const res = await apiFetch('/setuserpermissions', {
        body: { UserID: user.UserID, permissions: [...effectivePerms] },
      });
      const json = await res.json();
      if (json.ok) {
        showToast('success', 'Permissions saved');
        savedOverridesRef.current = overrides;
      } else {
        showToast('error', json.message || 'Failed to save permissions');
      }
    } catch (err) {
      showToast('error', 'Failed to save permissions');
    } finally {
      setIsSavingPerms(false);
    }
  };

  const userDataRef = useRef(userData);
  const backupUserDataRef = useRef(backupUserData);

  useEffect(() => {
    userDataRef.current = userData;
  }, [userData]);

  useEffect(() => {
    backupUserDataRef.current = backupUserData;
  }, [backupUserData]);

  const fieldUpdateListener = (msg)=>{
    if(msg.sheetName!==parentSheetName){
      //Only handle events for this sheet
      return;
    }
    //Get the Field and Status {ok: true/false, fieldName: fieldName}
    if(msg.ok){ //Update was successful
        //Update the fallback
        setBackupUserData({...backupUserDataRef.current, [msg.fieldName]: userDataRef.current[msg.fieldName]});
        showToast('success', 'Field saved', 2000);
    } else { //Check and log msg.error
        //Show error and revert to fallback
        setUserData({...userDataRef.current, [msg.fieldName]:backupUserDataRef.current[msg.fieldName]});
        showToast('error', msg.message || 'Failed to save. Change reverted.');
    }
  }


  useEffect(()=>{
    console.log(user);
    //Add event listeners to the websocket
    //Return a function to remove them when component dismounts
    if(socket.disconnected){
      console.log("Socket disconnected")
        socket.connect()
    }
    
    socket.on('update_record', fieldUpdateListener);
    //Two outcomes, not one. Since migration 057 an address that already has an
    //account elsewhere on the platform is NOT a collision — adding them here
    //gives that person a second dealership membership on the credential they
    //already own, and they keep their existing password. The only genuine
    //refusal is an address already in THIS dealership, which the server now
    //enforces at /adduseraccount and reports here so the form can say so before
    //the whole thing is filled in.
    socket.on('emailaddress-exists', (info)=>{
      setIsUnique(prev=>{
        return {...prev,
          EmailAddress: info?.alreadyMember ? 'member' : 'existing',
          MemberStatus: info?.memberStatus || null}
      });
    })

    socket.on('emailaddress-new', (info)=>{
      setIsUnique(prev=>{
        //Even a brand-new credential can already be a member here if the row was
        //created before Identities existed, so the flag is honoured either way.
        return {...prev,
          EmailAddress: info?.alreadyMember ? 'member' : true,
          MemberStatus: info?.memberStatus || null}
      });
    })
  
    return ()=>{
        socket.off('emailaddress-exists');
        socket.off('emailaddress-new');
        socket.off('update_record',fieldUpdateListener);
    }
  },[user]);

  const handleBlur = ({ target })=>{
    //Ignore changes to the EmailAddress field in edit mode
    if(target.name==="EmailAddress"){
      if(mode==="edit") return;
      socket.emit('check-emailaddress', {EmailAddress: userData.EmailAddress});
      setIsUnique(prev=>{
        return {...prev, EmailAddress: 'Pending'}
      });
    };

    //Do the same check as before submitting, except with only this input
    let err = ""; //Error message holder
    const attr = target.name;
    let regex = "";
    if(fields[attr] && fields[attr].regex){ //If the regex exists
      regex = new RegExp(fields[attr].regex); //Create the regex
      if(!regex.test(userData[attr])){ //Test against the entered data. If the regex test fails
        err = fields[attr].message; //store the err for later display to the user
      }  
    }
    if(fields[attr] && fields[attr].required && userData[attr]===""){ //If the field is required and empty
      err = fields[attr].displayName + " is required"; //Set err = "Field is required"
    }
    
    setErrors(prev=>{ //Update the errors state so that the error message is displayed
      return {...prev, [attr]:err}
    })

    if(err){
      //If there are errors, show errors and return. Return is only really
      //useful in edit mode because it skips the submission code
      target.focus();
      return;
    } //In add mode, nothing need be done beyond this point

    //This block referred to formData/backupFormData/setFormData and
    //formData.BrokerID — none of which exist here; the whole thing was pasted
    //from the broker/customer/seller modals, comment and all. This component's
    //state is userData/backupUserData/setUserData keyed on UserID. handleBlur is
    //wired to onBlur on the name inputs, so editing a user in edit mode and
    //clicking away threw ReferenceError: formData is not defined. Pre-existing,
    //found by a no-undef pass while removing the offline queue.
    if (mode === "edit" && !(userData[target.name] === backupUserData[target.name])) {
      const valueToSend = (target.name === "Phone1" || target.name === "Phone2")
        ? formatPhoneForStorage(target.value)
        : target.value;

      sendOrFail({
        DealershipID: globals.DealershipID,
        UserID: globals.userData.UserID,
        ownSheetName: parentSheetName,
        table: table_name,
        fieldName: target.name,
        fieldValue: valueToSend,
        primaryKeyName: primary_key,
        primaryKeyValue: userData.UserID
      }, socket, userData, backupUserData, setUserData)
    }
  }

  const validate = () => {
    let tempErrors = {};
    let regex;
    for(let attr of Object.keys(userData)){
      if(fields[attr] && fields[attr].regex){ //If the regex exists
        regex = new RegExp(fields[attr].regex);
        if(!regex.test(userData[attr])){ //If the regex test fails
          tempErrors[attr] = fields[attr].message;
        }  
      }
      if(fields[attr] && fields[attr].required && userData[attr]===""){ //If the field is required and empty
        tempErrors[attr] = fields[attr].displayName + " is required";
      }
    }

    //Check that at least 1 of the user roles is selected
    if(Object.values(roles).filter((role)=>role.Granted).length===0){
      tempErrors.Roles = "At least one role must be selected";
    }

    //Check that the emailaddress is unique
    //'member' is the only refusal. It used to be every falsy value, which meant
    //an address belonging to a colleague at ANOTHER dealership was rejected with
    //"already in use" — wrong since 057, and the reason a real person could not
    //be added.
    if(isUnique.EmailAddress==='member') {
      tempErrors.EmailAddress = isUnique.MemberStatus && isUnique.MemberStatus !== 'Active'
        ? `That address belongs to a ${isUnique.MemberStatus.toLowerCase()} account here — restore it instead.`
        : "Someone with that email address is already in this dealership";
    }
    if(isUnique.EmailAddress===false) tempErrors.EmailAddress = "Email Address already in use";
    if(isUnique.EmailAddress==="Pending") tempErrors.EmailAddress = "Please wait while we check the emailaddress";
    
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0; //Return true if there are no errors
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (isSubmitting) return;
    
    if (!validate()) {
      console.log("Validation failed: ", errors);
      return;
    }

    //Handle submission
    if(mode==="add"){ //Not a necessary check, but i'll leave it
      setIsSubmitting(true);
      
      try {
        //No password is generated here any more. The server sets one nobody is
        //ever told and emails the new user a link to choose their own — see
        //node/helpers/userInvite.js. UserPassword is still sent because the
        //addUser proc's JSON payload expects the key; the server overwrites it.
        userData.UserPassword = "";

        //Submit
        const response = await apiFetch("/adduseraccount", { body: {
                ...userData,
                Phone1: formatPhoneForStorage(userData.Phone1),
                Phone2: formatPhoneForStorage(userData.Phone2),
                //Membership-only model (migration 021): send the selected role IDs
                RoleIDs: Object.values(roles).filter((r) => r.Granted).map((r) => r.RoleID),
                //Per-user fine-tuning (migration 023): the full desired effective
                //set; the server stores only the deltas from the role base.
                permissions: [...effectivePerms],
                DealershipID: globals.DealershipID,
                UserID: globals.userData.UserID,
                ownSheetName: parentSheetName,
                profilePic
            } });

        const resObj = await response.json();

        if(response.ok){
            //Deliberately does NOT close here. The account exists, but whether
            //the new user can actually get into it depends on an email we
            //cannot confirm was delivered, so the admin is shown the outcome
            //and the link before this modal goes away.
            setInviteResult({
              email: userData.EmailAddress,
              inviteUrl: resObj.inviteUrl,
              emailWarning: resObj.emailWarning,
              smsWarning: resObj.smsWarning,
            });
            showToast(resObj.emailWarning ? 'warning' : 'success', resObj.message || 'User account created');
          //Call the callback for refetching
        } else if (resObj.alreadyMember) {
            //A deliberate refusal, not a fault. Shown against the field that
            //caused it rather than in a "Server Error" box telling someone to
            //try again later — retrying will refuse identically, and the thing
            //to do is either use a different address or restore the account
            //that is already here.
            setIsUnique(prev => ({ ...prev,
              EmailAddress: 'member',
              MemberStatus: resObj.status || null }));
            setErrors(prev => ({ ...prev, EmailAddress: resObj.message }));
            showToast('warning', resObj.message);
        } else {
          console.log(resObj);
          toggleMessageBox({
              on: true,
              title: "Server Error",
              message: "" + resObj.message + ". Please try again later.",
              buttons: ["OK"],
              clicked: ""
          });
        }
      } catch(err) {
        toggleMessageBox({
            on: true,
            title: "Network Error",
            message: err.message,
            buttons: ["OK"],
            clicked: ""
        });
      } finally {
        setIsSubmitting(false);
      }
    } else {
      onClose();
        //Call the callback to re-fetch customers
        //And set the newly added as the selected one
    }
  
  }; 

  const handleDelete = async (uid)=>{
    // Prevent multiple delete attempts
    if (isDeleting) return;
    
    //Check if the user is the currently logged in user
    //Not really necessary, but just in case the user is a smartass tech savvy
    //That tries to bypass the hidden delete button
    if(uid===UserID){
      toggleMessageBox({
        on: true,
        title: "Delete User",
        message: "You cannot delete yourself, dummy! Nice try though!",
        buttons: ["OK"],
        clicked: ""
      });
      return;
    }

    //Ask for confirmation
    let result = await getMsgBoxResult({
      on: true,
      title: "Confirm Delete User",
      message: `Are you sure you want to delete ${userData.LastName}'s account?`,
      buttons: ["Yes", "No"]
    });

    if(result==="No") return;

    //Confirm again
    result = await getMsgBoxResult({
      on: true,
      title: "Confirm Delete User",
      message: `This action is irreversible. Are you sure you want to delete ${userData.LastName}'s account?`,
      buttons: ["Yes", "Cancel"]
    });

    if(result==="Cancel") return;

    //Proceed to delete
    setIsDeleting(true);

    try{
      const response = await apiFetch('/deleteuser', { body: {
          DealershipID: globals.DealershipID,
          uid
        } });

      //Check that the response contains JSON, or if it's plain text before parsing
      if (response.headers.get("content-type")?.includes("application/json")) {
        const resObj = await response.json();
        if (response.ok) {
          //Close the modal
          onClose();
        } else {
          //Handle errors 500 and 401 separately
          if (response.status === 401) {
            toggleMessageBox({
                on: true,
                title: "Unauthorized",
                message: "Session Expired. Please login",
                buttons: ["OK"]
            });
            logOut();
          } else {
            toggleMessageBox({
              on: true,
              title: "Fetch Error",
              message: resObj.message,
              buttons: ["OK"],
              clicked: ""
            });
          }
        }
      } else {
        const resText = await response.text();
        toggleMessageBox({
          on: true,
          title: "Fetch Error",
          message: resText,
          buttons: ["OK"],
          clicked: ""
        });
      }
    } catch(err){
      toggleMessageBox({
        on: true,
        title: "Network Error",
        message: err.message,
        buttons: ["OK"],
        clicked: ""
      });
    } finally {
      setIsDeleting(false);
    }
  }

  return ReactDOM.createPortal(
    <Theme>
      <FormModeCtx.Provider value={mode}>
      {/* Dismissing this closes the whole modal: the account is already saved,
          so the form behind it is stale and its discard guard would be a lie. */}
      <InviteResultDialog result={inviteResult} onClose={() => { setInviteResult(null); onClose(); }} />
      <div id="edit-user-account" className="ua-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !isSubmitting && !isDeleting && !inviteResult) requestClose(); }}>
        <div className="ua-modal" role="dialog" aria-modal="true">
          <ModalTitleBar
            icon={UserCog}
            title={mode === "add" ? "Add User" : "Edit User Account"}
            onClose={requestClose}
            disabled={isSubmitting || isDeleting || !!inviteResult}
          />

          {/* Scrollable body */}
          <div className="ua-body">
            <section className="ua-section">
              <div className="ua-section-title">User Info</div>
              <div className="ua-grid">
                <div className="ua-field ua-col-span">
                  <label className="ua-label">Email Address</label>
                  <input
                    className="input"
                    name="EmailAddress"
                    value={userData.EmailAddress}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="name@example.com"
                    readOnly={mode !== 'add'}
                    disabled={isSubmitting}
                  />
                  {isUnique.EmailAddress==='member' && <p className='error'>
                    {isUnique.MemberStatus && isUnique.MemberStatus !== 'Active'
                      ? `That address belongs to a ${isUnique.MemberStatus.toLowerCase()} account in this dealership — restore it instead of creating a second one.`
                      : 'Someone with that email address is already in this dealership.'}
                  </p>}
                  {/* Not an error: they already work somewhere else on the
                      platform, so this adds a second membership to the account
                      they have. They keep their current password and are not
                      asked to set a new one. */}
                  {isUnique.EmailAddress==='existing' && <p className='text-sm'>
                    This person already has an Auto Dealer UG account. Adding them here gives
                    them access to this dealership as well — they sign in with their existing
                    password.
                  </p>}
                  {isUnique.EmailAddress===false && <p className='error'>This email address is taken. Please choose a different one.</p>}
                  {errors.EmailAddress && <p className="error">{errors.EmailAddress}</p>}
                </div>

                <div className="ua-field">
                  <label className="ua-label">First Name</label>
                  <EditableInput name="FirstName" value={userData.FirstName} onChange={handleChange} onBlur={handleBlur} placeholder="First Name" disabled={isSubmitting} />
                  {errors.FirstName && <p className="error">{errors.FirstName}</p>}
                </div>

                <div className="ua-field">
                  <label className="ua-label">Last Name</label>
                  <EditableInput name="LastName" value={userData.LastName} onChange={handleChange} onBlur={handleBlur} placeholder="Last Name" disabled={isSubmitting} />
                  {errors.LastName && <p className="error">{errors.LastName}</p>}
                </div>

                <div className="ua-field">
                  <label className="ua-label">Phone 1</label>
                  <EditableInput name="Phone1" value={userData.Phone1} onChange={handleChange} onBlur={handleBlur} placeholder="Phone 1" disabled={isSubmitting} />
                  {errors.Phone1 && <p className="error">{errors.Phone1}</p>}
                </div>

                <div className="ua-field">
                  <label className="ua-label">Phone 2</label>
                  <EditableInput name="Phone2" value={userData.Phone2} onChange={handleChange} onBlur={handleBlur} placeholder="Phone 2" disabled={isSubmitting} />
                  {errors.Phone2 && <p className="error">{errors.Phone2}</p>}
                </div>
              </div>
            </section>

            <section className="ua-section">
              <div className="ua-section-title">Roles</div>
              <UserRoles
                UserID = {user?.UserID || null}
                roles={roles}
                handleRoleChange={handleRoleChange}
                mode={mode}
                setRoles={setRoles}
                backupRoles={backupRoles}
                setBackupRoles={setBackupRoles}
                disabled={isSubmitting}
              />
              {errors.Roles && <p className="error">{errors.Roles}</p>}
            </section>

            {catalog.length > 0 && (
              <section className="ua-section">
                <div className="ua-section-head">
                  <div className="ua-section-title" style={{ margin: 0 }}>Fine-tune permissions</div>
                  <Button type="button" variant="soft" size="1" onClick={() => setShowPerms((s) => !s)}>
                    {showPerms ? 'Hide' : (mode === 'add' ? 'Next: fine-tune ▾' : 'Show ▾')}
                  </Button>
                </div>
                <Text as="p" size="1" color="gray" style={{ marginTop: 4 }}>
                  Roles set the defaults below. Tick or untick to grant or deny a
                  specific permission for this user — overrides win over the role.
                </Text>
                {showPerms && (
                  <div className="ua-permgrid">
                    <PermissionGrid
                      catalog={catalog}
                      checked={effectivePerms}
                      baseSet={roleBase}
                      onToggle={handlePermToggle}
                      disabled={!canEditPerms || isSubmitting}
                    />
                    {mode === 'edit' && canEditPerms && (
                      <Flex justify="end" mt="3">
                        <Button type="button" variant="soft" size="1" onClick={handleSavePermissions} disabled={isSavingPerms}>
                          {isSavingPerms ? (<Flex align="center" gap="2"><Spinner size="1" /> Saving...</Flex>) : 'Save permissions'}
                        </Button>
                      </Flex>
                    )}
                  </div>
                )}
              </section>
            )}
          </div>

          {/* Sticky footer actions */}
          <div className="ua-footer">
            {mode === "edit" && UserID !== userData.UserID && (
              <Button variant="soft" color="red" onClick={() => handleDelete(user.UserID)} disabled={isDeleting || isSubmitting}>
                {isDeleting ? (<Flex align="center" gap="2"><Spinner size="1" /> Deleting...</Flex>) : "Delete"}
              </Button>
            )}
            <div className="ua-footer-spacer" />
            <Button variant="soft" color="gray" onClick={requestClose} disabled={isSubmitting || isDeleting}>
              <CancelIcon size={ICON_SIZE} />
              {mode === "add" ? "Cancel" : "Close"}
            </Button>
            {mode === 'add' && (
              <Button variant="solid" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? (<Flex align="center" gap="2"><Spinner size="1" /> Submitting...</Flex>) : "Submit"}
              </Button>
            )}
          </div>
        </div>
      </div>
      </FormModeCtx.Provider>
    </Theme>
  , document.body);
};

function UserRoles({ UserID, roles, backupRoles, handleRoleChange, mode, setRoles, setBackupRoles, disabled }) {
  const globals = useContext(GlobalContext);
  const { showToast } = useToast();
  const canEdit = globals.can('users.manage') && globals.userData.UserID !== UserID;

  //Refs to the latest state so the async /setuserroles handler reads current
  //values (handleRoleChange's setState hasn't flushed when the handler runs).
  const rolesRef = useRef(roles);
  const backupRolesRef = useRef(backupRoles);

  useEffect(() => { rolesRef.current = roles; }, [roles]);
  useEffect(() => { backupRolesRef.current = backupRoles; }, [backupRoles]);

  //Membership-only model (migration 021): a checkbox toggle replaces the
  //user's whole membership set via POST /setuserroles. Optimistic toggle with
  //rollback + toast on failure (self-edit 403, lockout 409, etc.). The server
  //broadcasts a replace_record that refreshes the accounts list.
  const handleCheckBoxChange = async ({ target }) => {
    const { name, checked } = target;
    handleRoleChange(name); //optimistic toggle in the parent's roles state

    if (mode === "add") return; //creation sends RoleIDs on submit
    if ((checked ? true : false) === backupRolesRef.current[name].Granted) return; //no net change

    const RoleIDs = Object.entries(rolesRef.current)
      .filter(([rn, r]) => (rn === name ? checked : r.Granted))
      .map(([, r]) => r.RoleID);

    try {
      const res = await apiFetch('/setuserroles', { body: { UserID, RoleIDs } });
      const json = await res.json();
      if (json.ok) {
        setBackupRoles({ ...backupRolesRef.current, [name]: { RoleID: rolesRef.current[name].RoleID, Granted: checked } });
      } else {
        setRoles({ ...rolesRef.current, [name]: backupRolesRef.current[name] });
        showToast('error', json.message || 'Failed to update roles. Change reverted.');
      }
    } catch (err) {
      setRoles({ ...rolesRef.current, [name]: backupRolesRef.current[name] });
      showToast('error', 'Failed to update roles. Change reverted.');
    }
  };

  const roleNames = Object.keys(roles);
  if (roleNames.length === 0) {
    return <p className="ua-empty">No roles defined for this dealership yet.</p>;
  }

  return (
    <div className="ua-roles">
      {roleNames.map((role) => (
        <label key={role} className={`ua-role-chip${roles[role].Granted ? ' is-on' : ''}${(!canEdit || disabled) ? ' is-disabled' : ''}`}>
          <input
            disabled={!canEdit || disabled}
            name={role}
            type="checkbox"
            checked={roles[role].Granted}
            onChange={handleCheckBoxChange}
            value={roles[role].Granted ? true : false}
          />
          <span>{role}</span>
        </label>
      ))}
    </div>
  );
}
export default UserAccountModal;